import React, { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import AttractionDirectoryTable from '../components/directory/AttractionDirectoryTable';
import SiteHeader from '../components/directory/SiteHeader';
import PageContainer from '../components/layout/PageContainer';
import { getCityAttractions, getDirectoryCountry } from '../services/dataStore';
import { useLocale } from '../context/LocaleContext';
import { isSupportedLocale } from '../utils/lang-route';

const CityDirectoryPage: React.FC = () => {
  const { lang, id, citySlug } = useParams<{
    lang: string;
    id: string;
    citySlug: string;
  }>();
  const { locale, setLocale } = useLocale();
  const effectiveLocale = isSupportedLocale(lang) ? lang : locale;
  const isEn = effectiveLocale === 'en';

  useEffect(() => {
    if (isSupportedLocale(lang) && lang !== locale) {
      setLocale(lang);
    }
  }, [lang, locale, setLocale]);

  const payload = useMemo(() => {
    if (!id || !citySlug) return null;
    return getCityAttractions(id, citySlug, effectiveLocale);
  }, [id, citySlug, effectiveLocale]);

  const directoryCountry = useMemo(
    () => (id ? getDirectoryCountry(id, effectiveLocale) : null),
    [id, effectiveLocale]
  );

  if (!payload || !directoryCountry) {
    return (
      <PageContainer>
        <SiteHeader />
        <p className="text-[var(--dir-muted)] mt-8">
          {isEn ? 'City not found.' : '未找到该城市。'}
        </p>
        <Link to={`/${effectiveLocale}`} className="dir-link-coral text-sm mt-4 inline-block">
          ← {isEn ? 'Home' : '返回首页'}
        </Link>
      </PageContainer>
    );
  }

  const countryName = isEn ? directoryCountry.nameEn : directoryCountry.name;
  const countLine = isEn
    ? `${payload.attractions.length} official attraction websites`
    : `${payload.attractions.length} 个景点官方网站`;

  return (
    <PageContainer>
      <SiteHeader />
      <nav className="text-sm text-[var(--dir-muted)] mt-6 mb-4">
        <Link to={`/${effectiveLocale}`} className="dir-link">
          {isEn ? 'Home' : '首页'}
        </Link>
        <span className="mx-2">/</span>
        <Link to={`/${effectiveLocale}/country/${id}`} className="dir-link">
          {countryName}
        </Link>
        <span className="mx-2">/</span>
        <span>{payload.cityDisplayName}</span>
      </nav>

      <h1 className="text-3xl font-semibold tracking-tight">{payload.cityDisplayName}</h1>
      <p className="text-[var(--dir-muted)] mt-2 mb-8">{countLine}</p>

      <AttractionDirectoryTable attractions={payload.attractions} />

      <p className="text-xs text-[var(--dir-muted)] mt-8 max-w-xl">
        {isEn
          ? 'All links point to official attraction websites when available. No resellers.'
          : '链接尽量指向景点官网，非 OTA 或代理。请自行核对后再购票。'}
      </p>
    </PageContainer>
  );
};

export default CityDirectoryPage;
